import { X } from "lucide-react";

import { GENRE_GROUPS, MAX_GENRES, genreLabel } from "@/constants/genres";

type Props = {
  value: string[];
  onChange: (genres: string[]) => void;
  id?: string;
};

/** Add genres from a curated dropdown; selected genres show as removable chips. */
export default function GenrePicker({ value, onChange, id = "genre-picker" }: Props) {
  const selected = new Set(value.map((genre) => genre.toLowerCase()));
  const full = value.length >= MAX_GENRES;
  return (
    <div className="space-y-2">
      <select
        id={id}
        className="mt-1 block w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground"
        value=""
        disabled={full}
        aria-label="Add a genre"
        onChange={(event) => {
          const next = event.target.value;
          if (next && !selected.has(next)) onChange([...value, next]);
        }}
      >
        <option value="">{full ? `Up to ${MAX_GENRES} genres` : "Add a genre…"}</option>
        {GENRE_GROUPS.map((group) => (
          <optgroup key={group.label} label={group.label}>
            {group.genres.map((label) => (
              <option key={label} value={label.toLowerCase()} disabled={selected.has(label.toLowerCase())}>
                {label}
              </option>
            ))}
          </optgroup>
        ))}
      </select>
      {value.length > 0 && (
        <ul className="flex flex-wrap gap-2" aria-label="Selected genres">
          {value.map((genre) => (
            <li key={genre} className="inline-flex items-center gap-1 rounded-full border border-primary/40 bg-primary/10 px-3 py-1 text-sm text-foreground">
              {genreLabel(genre)}
              <button
                type="button"
                className="rounded-full p-0.5 hover:bg-primary/20"
                aria-label={`Remove ${genreLabel(genre)}`}
                onClick={() => onChange(value.filter((item) => item !== genre))}
              >
                <X size={14} aria-hidden="true" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
